import { useRegisterSW } from 'virtual:pwa-register/react';
import { useServidorInalcancavel } from './Conexao.js';

/**
 * De quanto em quanto tempo perguntar ao servidor se há versão nova.
 *
 * O navegador procura um `sw.js` novo em navegação de página e, por conta
 * própria, mais ou menos a cada 24h. Nenhum dos dois serve para um PWA
 * instalado: quem abre o app pelo ícone não navega, e 24h é tempo demais —
 * subiram três versões num dia só em 16/10/2026.
 */
const INTERVALO_DA_CHECAGEM_MS = 30 * 60 * 1000;

/**
 * O aviso de versão nova, em qualquer tela.
 *
 * **O problema que isto resolve:** o conserto morava em Perfil → Sobre, e quem
 * não ia lá nunca recebia versão nova. Pior, o aviso de lá só aparecia depois
 * de a API responder uma versão diferente — ou seja, só depois de o problema já
 * estar instalado. Aviso que depende de a pessoa ir procurar não é aviso.
 *
 * **Por que pergunta e não recarrega sozinho.** Com `autoUpdate` o worker novo
 * assumia em silêncio, mas a página aberta continuava com os arquivos velhos
 * até um recarregamento — o clássico "uma visita atrasado": abre e vê o antigo,
 * fecha, abre de novo e aí sim recebe o novo. Recarregar sozinho consertaria
 * isso e criaria outro: o cadastro de garrafa carrega foto e texto digitado, e
 * puxar o tapete no meio disso perde o trabalho da pessoa. Então o app avisa e
 * espera o toque — perda de um toque contra perda de uma garrafa inteira.
 *
 * **Fica calado sem conexão**, pelos dois motivos de sempre: anunciar novidade
 * a quem não alcança o servidor é ruído, e recarregar com a rede fora é a
 * maneira mais fácil de abrir o app em branco. É também o que evita duas faixas
 * empilhadas no mesmo canto da tela.
 */
export function AvisoDeNovaVersao() {
  const inalcancavel = useServidorInalcancavel();

  const {
    needRefresh: [temVersaoNova],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registro) {
      if (!registro) return;
      perguntarDeVezEmQuando(registro);
    },
  });

  if (!temVersaoNova || inalcancavel) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 px-4 pb-2"
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3 rounded-xl border border-adega-gold/40 bg-adega-surface-2/95 px-4 py-3 text-sm shadow-lg backdrop-blur">
        <span aria-hidden className="text-base">
          ✨
        </span>
        <p className="flex-1 leading-snug">
          <strong className="font-semibold">Tem uma versão nova do app.</strong>{' '}
          <span className="text-adega-muted">Atualizar leva um segundo.</span>
        </p>
        {/* `true` recarrega a página depois de o worker novo assumir: sem isso a
            tela continuaria com os arquivos antigos, que é o bug de origem. */}
        <button
          type="button"
          className="shrink-0 rounded-lg bg-adega-wine px-3 py-1.5 text-xs font-semibold text-white active:bg-adega-wine-light"
          onClick={() => void updateServiceWorker(true)}
        >
          Atualizar
        </button>
      </div>
    </div>
  );
}

/**
 * Pergunta por versão nova de tempo em tempo e ao voltar para o app.
 *
 * O `visibilitychange` é o que realmente importa no celular: o PWA instalado
 * passa dias em segundo plano, e o momento em que a pessoa volta para ele é
 * exatamente quando um intervalo que estava congelado não ajudaria.
 *
 * Fora de função de componente de propósito: quem assina é o registro do
 * service worker, que existe uma vez por aba e não acompanha a vida do React.
 */
function perguntarDeVezEmQuando(registro: ServiceWorkerRegistration) {
  const perguntar = () => {
    // `update()` lança quando a rede está fora. Não é erro para mostrar a
    // ninguém: a próxima pergunta vem em trinta minutos.
    void registro.update().catch(() => undefined);
  };

  setInterval(perguntar, INTERVALO_DA_CHECAGEM_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') perguntar();
  });
}
